import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { CalendarDateTemplateEvent } from 'primereact/calendar'

type Props = {
  date: CalendarDateTemplateEvent
  reservedDates: Date[]
}

/**
 * ReservedDateTemplate component renders a calendar date template with reserved dates.
 * If the given date is in the reserved dates array, it is displayed as a disabled button with a tooltip.
 *
 * @param {CalendarDateTemplateEvent} date - The date object containing day, month, and year.
 * @param {Date[]} reservedDates - An array of reserved Date objects.
 * @returns {JSX.Element} The rendered component.
 */
export const ReservedDateTemplate: React.FC<Props> = ({
  date,
  reservedDates,
}) => {
  const { t } = useTranslation()

  // Create a date object from the given date
  const currentDate = new Date(date.year, date.month, date.day)

  // Check if currentDate is in the reservedDates array
  const isReserved = reservedDates.some(reservedDate => {
    return (
      currentDate.getDate() === reservedDate.getDate() &&
      currentDate.getMonth() === reservedDate.getMonth() &&
      currentDate.getFullYear() === reservedDate.getFullYear()
    )
  })

  return isReserved ? (
    <Button
      disabled
      className="p-button p-component p-button-text p-button-plain w-full h-full flex align-items-center justify-content-center line-through"
      tooltip={t('common.tooltips.reserved')}
      tooltipOptions={{ position: 'top' }}
    >
      {date.day}
    </Button>
  ) : (
    <>{date.day}</>
  )
}
